import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, useReducedMotion, AnimatePresence } from "framer-motion";
import * as Lucide from "lucide-react";
import { ArrowUpRight, ArrowRight, Sparkles, Code2, Brain, BarChart3, TrendingUp, Factory, ShieldCheck, Plus, Quote, Users, FolderKanban, CheckCircle2 } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";
import useMouseParallax from "@/hooks/useMouseParallax";
import { apiClient } from "@/config/api";
import { WINGS, PROCESS_STEPS, TECH_GROUPS, INDUSTRIES, FAQS, TEAM } from "@/data/site";

const WingIcons = { Code2, Brain, BarChart3, TrendingUp, Factory, ShieldCheck };
const MOTION_EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const staggerGroup = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

const heroWords = [
  { text: "We" },
  { text: "solve" },
  { text: "business", className: "italic font-normal text-[#4D8BFF]" },
  { text: "problems" },
  { text: "with" },
  { text: "intelligent", className: "shimmer-text" },
  { text: "technology.", className: "gradient-text" },
];

const EMPTY_LIVE_METRICS = {
  total_visitors: null,
  active_projects: null,
  projects_delivered: null,
};

function toMetricNumber(value) {
  const next = Number(value);
  return Number.isFinite(next) && next >= 0 ? next : 0;
}

function normalizeLiveMetrics(value) {
  return {
    total_visitors: value?.totalVisitors ?? value?.total_visitors ?? null,
    active_projects: toMetricNumber(value?.activeProjects ?? value?.active_projects),
    projects_delivered: toMetricNumber(value?.projectsDelivered ?? value?.projects_delivered),
  };
}

function AnimatedMetricNumber({ value }) {
  const reduceMotion = useReducedMotion();
  const [displayValue, setDisplayValue] = useState(0);
  const previousValue = useRef(0);

  useEffect(() => {
    if (value === null || value === undefined) return undefined;
    const target = toMetricNumber(value);
    if (reduceMotion) {
      previousValue.current = target;
      setDisplayValue(target);
      return undefined;
    }

    const start = previousValue.current;
    const startedAt = performance.now();
    let frameId;

    const tick = (now) => {
      const progress = Math.min((now - startedAt) / 620, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(start + (target - start) * eased));
      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      } else {
        previousValue.current = target;
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [value, reduceMotion]);

  if (value === null || value === undefined) return <span aria-busy="true">...</span>;
  return <span>{displayValue.toLocaleString()}</span>;
}

/* ========================================================================
   01. HERO — cinematic, mouse-parallax, magnetic CTAs, animated orbs
   ======================================================================== */
function Hero() {
  const mouse = useMouseParallax();
  const reduceMotion = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative pt-28 sm:pt-32 pb-8 overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 bg-grid opacity-40"/>
      {/* Animated gradient orbs (mouse-responsive) */}
      <div
        className="orb"
        style={{
          width: 520, height: 520,
          background: "radial-gradient(circle, #1E6BFF 0%, transparent 70%)",
          opacity: 0.35,
          left: `${18 + mouse.x * 18}%`, top: `${3 + mouse.y * 12}%`,
          transition: "left 800ms ease-out, top 800ms ease-out",
        }}
      />
      <div
        className="orb"
        style={{
          width: 420, height: 420,
          background: "radial-gradient(circle, #4D8BFF 0%, transparent 70%)",
          opacity: 0.22,
          right: `${7 + mouse.x * 13}%`, bottom: `${3 + (1 - mouse.y) * 14}%`,
          transition: "right 1000ms ease-out, bottom 1000ms ease-out",
        }}
      />
      {/* SVG mesh lines */}
      <svg className="absolute inset-0 w-full h-full opacity-30 pointer-events-none" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ln" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1E6BFF" stopOpacity="0"/>
            <stop offset="50%" stopColor="#4D8BFF" stopOpacity="0.5"/>
            <stop offset="100%" stopColor="#1E6BFF" stopOpacity="0"/>
          </linearGradient>
        </defs>
        {[...Array(6)].map((_, i) => (
          <motion.line
            key={i}
            x1="0" y1={`${10 + i * 18}%`}
            x2="100%" y2={`${5 + i * 15}%`}
            stroke="url(#ln)" strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.7 }}
            transition={{ duration: 2.2, delay: i * 0.15, ease: "easeOut" }}
          />
        ))}
      </svg>

      <motion.div style={{ y: y1, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <motion.div
            variants={staggerGroup}
            initial="hidden"
            animate="visible"
            className="lg:col-span-8"
          >
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.65, ease: MOTION_EASE }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass text-[12px] text-[#C9D2E0]"
            >
              <span className="dot-pulse"/> A technology studio for businesses that need more than templates
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.75, ease: MOTION_EASE }}
              className="font-display hero-heading mt-6 max-w-4xl text-[clamp(2rem,5vw,3.5rem)] leading-[1.12] tracking-normal font-semibold"
            >
              {heroWords.map((word, i) => (
                <motion.span
                  key={`${word.text}-${i}`}
                  initial={reduceMotion ? false : { opacity: 0, y: 22, filter: "blur(8px)" }}
                  animate={reduceMotion ? false : { opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 0.58, delay: 0.24 + i * 0.055, ease: MOTION_EASE }}
                  className={`inline-block mr-[0.28em] ${word.className || ""}`}
                >
                  {word.text}
                </motion.span>
              ))}
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.75, ease: MOTION_EASE }}
              className="mt-6 text-[15.5px] sm:text-[18px] text-[#9AA3B8] max-w-xl leading-[1.55]"
            >
              DortX builds high-quality AI solutions, software, and automation systems that solve real business problems. Every project is engineered with precision, tested thoroughly, and delivered on time.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.7, ease: MOTION_EASE }}
              className="mt-6 flex flex-wrap items-center gap-3"
            >
              <Link to="/services" data-testid="hero-cta-services" className="btn-ghost">
                Explore services <ArrowRight size={16}/>
              </Link>
              <MagneticButton strength={14}>
                <Link to="/contact" data-testid="hero-cta-start" className="btn-primary">
                  Start a project <ArrowUpRight size={16}/>
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>

          {/* Right side: floating technology orbit (decorative) */}
          <motion.div
            style={{
              y: y2,
              x: reduceMotion ? 0 : (mouse.x - 0.5) * 34,
            }}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.9, rotate: -4 }}
            animate={reduceMotion ? false : { opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, delay: 0.45, ease: MOTION_EASE }}
            className="hidden lg:block lg:col-span-4 relative h-[440px]"
          >
            <div className="absolute inset-0 flex items-center justify-center hero-orbit-stage">
              {/* Concentric rings */}
              {[180, 280, 380].map((s, i) => (
                <div key={i} className="absolute rounded-full border border-[#1E6BFF]/15" style={{ width: s, height: s }}/>
              ))}
              {/* Centre core */}
              <motion.div
                animate={reduceMotion ? {} : {
                  scale: [1, 1.08, 1],
                  boxShadow: [
                    "0 0 44px -12px rgba(30,107,255,0.6)",
                    "0 0 76px -8px rgba(77,139,255,0.9)",
                    "0 0 44px -12px rgba(30,107,255,0.6)",
                  ],
                }}
                transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-20 h-20 rounded-2xl glass flex items-center justify-center"
              >
                <Sparkles className="text-[#4D8BFF]" size={26}/>
              </motion.div>
              {/* Orbiting nodes */}
              {[
                { icon: "Code2", r: 90, d: 0 },
                { icon: "Brain", r: 90, d: 5 },
                { icon: "BarChart3", r: 140, d: 2 },
                { icon: "TrendingUp", r: 140, d: 9 },
                { icon: "ShieldCheck", r: 190, d: 4 },
                { icon: "Database", r: 190, d: 13 },
              ].map((o, i) => {
                const Icon = Lucide[o.icon];
                return (
                  <div key={i} className="absolute orbit" style={{ "--r": `${o.r}px`, animationDelay: `-${o.d}s` }}>
                    <div className="hero-orbit-node w-10 h-10 rounded-xl glass flex items-center justify-center text-[#C9D2E0]" style={{ animationDelay: `${i * 0.45}s` }}>
                      <Icon size={15}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Bottom strip — values */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 1 }}
          className="mt-6 pt-6 border-t border-white/8 grid grid-cols-2 sm:grid-cols-4 gap-5 sm:gap-5"
        >
          {[
            { k: "Team", v: "Small & focused" },
            { k: "Service wings", v: "Six disciplines" },
            { k: "Engagement", v: "Outcome-Driven" },
            { k: "IP ownership", v: "100% yours" },
          ].map((x, i) => (
            <div key={i}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B7385]">{x.k}</div>
              <div className="mt-2 font-display text-[18px] text-white">{x.v}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ========================================================================
   02. DORTX LIVE — compact real-time activity strip
   ======================================================================== */
function DortXLive() {
  const [metrics, setMetrics] = useState(EMPTY_LIVE_METRICS);
  const metricItems = [
    { label: "Total Visitors", value: metrics.total_visitors, Icon: Users },
    { label: "Active Projects", value: metrics.active_projects, Icon: FolderKanban },
    { label: "Projects Delivered", value: metrics.projects_delivered, Icon: CheckCircle2 },
  ];

  useEffect(() => {
    let cancelled = false;

    const applyMetrics = (data) => {
      if (!cancelled) setMetrics(normalizeLiveMetrics(data));
    };

    const fetchMetrics = async () => {
      try {
        const response = await apiClient.get("/live/stats");
        applyMetrics(response.data);
      } catch {
        // Keep the last known values visible if the metrics request fails.
      }
    };

    fetchMetrics();
    const statsTimer = window.setInterval(fetchMetrics, 10000);

    return () => {
      cancelled = true;
      window.clearInterval(statsTimer);
    };
  }, []);

  return (
    <section className="relative py-5 sm:py-7">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.55, ease: MOTION_EASE }}
          className="dortx-live-shell relative overflow-hidden rounded-2xl px-4 py-4 sm:px-6 sm:py-4 lg:px-7"
        >
          <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none"/>
          <div className="absolute -left-16 top-1/2 h-36 w-36 -translate-y-1/2 rounded-full bg-[#1E6BFF]/20 blur-[54px] pointer-events-none"/>
          <div className="absolute -right-14 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full bg-[#4D8BFF]/12 blur-[48px] pointer-events-none"/>

          <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center justify-between gap-4 lg:w-52 lg:justify-start">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#4D8BFF]">DORTX LIVE</div>
                <div className="mt-1 text-[12px] text-[#6B7385]">Real-time activity</div>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-emerald-300 shadow-[0_0_24px_rgba(52,211,153,0.12)]">
                <span className="dot-pulse" aria-hidden="true"/>
                LIVE
              </div>
            </div>

            <div className="grid grid-cols-3 gap-1 sm:gap-2 lg:flex-1">
              {metricItems.map(({ label, value, Icon }, index) => (
                <motion.div
                  key={label}
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.22, ease: MOTION_EASE }}
                  className="dortx-live-metric group relative min-h-[82px] rounded-xl px-2 py-3 text-center sm:px-4"
                >
                  <div className="mb-2 flex items-center justify-center">
                    <span className="dortx-live-icon inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#1E6BFF]/10 text-[#4D8BFF]" style={{ animationDelay: `${index * 0.35}s` }}>
                      <Icon size={14}/>
                    </span>
                  </div>
                  <div className="font-display text-[clamp(1.7rem,4.4vw,2.75rem)] leading-none font-bold text-white tabular-nums">
                    <AnimatedMetricNumber value={value}/>
                  </div>
                  <div className="mt-2 text-[11px] sm:text-[12px] leading-tight text-[#9AA3B8]">{label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ========================================================================
   03. INTRO — "Why DortX exists" — quiet, narrative, full-width statement
   ======================================================================== */
function WhyExist() {
  const ref = useRef(null);

  return (
    <section ref={ref} className="relative py-14 lg:py-16">
      <motion.div
        variants={staggerGroup}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.35 }}
        className="max-w-5xl mx-auto px-6 lg:px-8"
      >
        <div className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF] mb-6">— Why we exist</div>

        <motion.p variants={fadeUp} transition={{ duration: 0.65, ease: MOTION_EASE }} className="font-display text-[28px] sm:text-[38px] lg:text-[48px] leading-[1.15] font-medium">
          We kept seeing the same pattern.
        </motion.p>

        <div className="grid lg:grid-cols-2 gap-x-16 gap-y-8 mt-6">
          <motion.p variants={fadeUp} transition={{ duration: 0.65, ease: MOTION_EASE }} className="text-[15.5px] sm:text-[16px] text-[#C9D2E0] leading-[1.7]">
            Companies investing heavily in software — and getting back generic outputs. Slow products. Dashboards no one opens. AI experiments that never see production. Marketing money that disappears into the void.
          </motion.p>
          <motion.p variants={fadeUp} transition={{ duration: 0.65, ease: MOTION_EASE }} className="text-[15.5px] sm:text-[16px] text-[#9AA3B8] leading-[1.7]">
            DortX was started to be the opposite of that. A small, focused team that treats every engagement like our own product — with care, ownership and the standards we'd expect for ourselves.
          </motion.p>
        </div>

        <motion.div variants={fadeUp} transition={{ duration: 0.6, ease: MOTION_EASE }} className="mt-6 flex items-center gap-5">
          <div className="h-px flex-1 bg-gradient-to-r from-[#1E6BFF]/40 to-transparent"/>
          <Link to="/about" className="text-[13px] text-[#C9D2E0] hover:text-white flex items-center gap-2">
            Read the full story <ArrowRight size={14}/>
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ========================================================================
   03. PROBLEMS — "The friction we remove" — split, asymmetric
   ======================================================================== */
function Problems() {
  const items = [
    { n: "I", t: "Software that nobody uses", d: "Generic, slow products built by teams who never asked the right questions." },
    { n: "II", t: "AI experiments that never ship", d: "Pilots, demos, decks. Then silence. The model never reaches a real workflow." },
    { n: "III", t: "Dashboards full of noise", d: "Vanity metrics nobody trusts. Decisions still made on instinct, not evidence." },
    { n: "IV", t: "Growth that doesn't compound", d: "Ad spend without measurement. SEO without architecture. Brand without positioning." },
  ];

  return (
    <section className="relative py-14">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.65, ease: MOTION_EASE }}
            className="lg:col-span-5 lg:sticky lg:top-32 self-start"
          >
            <div className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF]">— What we remove</div>
            <h2 className="font-display section-heading font-semibold mt-5">
              The four frictions <br/> we hear about <br/>
              <span className="gradient-text">every week</span>.
            </h2>
            <p className="mt-6 text-[15.5px] text-[#9AA3B8] leading-relaxed max-w-md">
              Most of our engagements start with one of these stories. They sound different on the surface — but the underlying problem is almost always the same.
            </p>
          </motion.div>

          <div className="lg:col-span-7 space-y-3">
            {items.map((it, i) => (
              <motion.div
                key={it.n}
                initial={{ opacity: 0, x: 36, y: 10 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                whileHover={{ y: -8, rotateX: 1.2, rotateY: -1.2, boxShadow: "0 22px 55px -32px rgba(77,139,255,0.65)" }}
                whileTap={{ scale: 0.985, y: -2 }}
                transition={{ duration: 0.58, delay: i * 0.11, ease: MOTION_EASE }}
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                }}
                className="spotlight glass rounded-2xl p-5 flex items-start gap-5 group motion-card"
              >
                <div className="font-display text-[34px] text-[#243049] leading-none">{it.n}</div>
                <div className="flex-1">
                  <h3 className="font-display text-[18px] font-semibold text-white">{it.t}</h3>
                  <p className="mt-2 text-[14px] text-[#9AA3B8] leading-relaxed">{it.d}</p>
                </div>
                <ArrowUpRight className="text-[#4D8BFF] opacity-0 group-hover:opacity-100 transition" size={18}/>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========================================================================
   04. APPROACH — How we work as a system
   ======================================================================== */
function Approach() {
  return (
    <section className="relative py-14 lg:py-16 overflow-hidden">
      <div className="absolute inset-0 hair-diag opacity-35"/>
      <div className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#1E6BFF]/10 blur-[90px]"/>
      <motion.div
        variants={staggerGroup}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        className="relative max-w-7xl mx-auto px-6 lg:px-8"
      >
        <div className="text-center max-w-4xl mx-auto">
          <motion.div variants={fadeUp} transition={{ duration: 0.55, ease: MOTION_EASE }} className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF] mb-6">— Our approach</motion.div>
          <motion.h2 variants={fadeUp} transition={{ duration: 0.65, ease: MOTION_EASE }} className="font-display section-heading font-medium">
            One small team. <span className="text-[#4D8BFF]">Six disciplines.</span> <br className="hidden sm:block"/>
            Working as a single system.
          </motion.h2>
          <motion.p variants={fadeUp} transition={{ duration: 0.65, ease: MOTION_EASE }} className="mt-5 text-[15.5px] text-[#9AA3B8] max-w-2xl mx-auto leading-relaxed">
            Most agencies separate engineering, design, AI, growth, industrial automation and security into different silos. We don't. They reinforce each other - so we run them as one practice, on one project, with one team.
          </motion.p>
        </div>

      </motion.div>
    </section>
  );
}

/* ========================================================================
   05. WINGS - compact home overview cards
   ======================================================================== */
function WingCard({ wing, index }) {
  const Icon = WingIcons[wing.icon] || Sparkles;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      whileHover={{ y: -10, boxShadow: "0 28px 70px -42px rgba(77,139,255,0.75)" }}
      whileTap={{ scale: 0.985, y: -3 }}
      transition={{ duration: 0.58, delay: index * 0.09, ease: MOTION_EASE }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
      }}
      className="wing-overview-card spotlight rounded-2xl border border-white/8 bg-white/[0.02] p-5 sm:p-6 min-h-[260px] flex flex-col overflow-hidden relative"
    >
      <motion.div
        className="wing-overview-num"
        aria-hidden="true"
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={inView ? { opacity: 0.55, y: 0, scale: 1 } : {}}
        transition={{ duration: 0.75, delay: 0.18 + index * 0.08, ease: MOTION_EASE }}
      >
        {wing.number}
      </motion.div>

      <div className="relative z-10 flex h-full flex-col">
        <div className="flex items-center justify-between gap-4">
          <div className="wing-card-icon w-12 h-12 rounded-xl glass flex items-center justify-center text-[#4D8BFF] shrink-0">
            <Icon size={21}/>
          </div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-[#6B7385] whitespace-nowrap">Wing {wing.number}</div>
        </div>

        <h3 className="font-display mt-7 text-[22px] sm:text-[25px] leading-[1.15] font-semibold">{wing.name}</h3>
        <p className="mt-3 text-[14.5px] text-[#9AA3B8] leading-relaxed">{wing.short}</p>

        <div className="mt-auto pt-7">
          <Link
            to={`/services#${wing.id}`}
            data-testid={`home-wing-link-${wing.id}`}
            className="inline-flex btn-ghost"
          >
            Explore this wing <ArrowUpRight size={15}/>
          </Link>
        </div>
      </div>
    </motion.article>
  );
}

function Wings() {
  return (
    <section className="relative py-14 lg:py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF] mb-5">— The six wings</div>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div>
            <h2 className="font-display section-heading font-semibold max-w-4xl">
              Six focused practices. <br/>
              One connected system.
            </h2>
            <p className="mt-5 text-[15.5px] text-[#9AA3B8] leading-relaxed max-w-2xl">
              A quick overview of what we do. Each wing opens into the full service detail when you need the depth.
            </p>
          </div>
          <Link to="/services" className="btn-ghost shrink-0">
            View all service details <ArrowRight size={15}/>
          </Link>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {WINGS.map((w, i) => <WingCard key={w.id} wing={w} index={i}/>)}
      </div>
    </section>
  );
}

/* ========================================================================
   06. PROCESS — vertical timeline with progress line
   ======================================================================== */
function ProcessTimeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 30%"] });
  const lineH = useTransform(scrollYProgress, [0, 1], ["0%", "100%"], { clamp: true });

  return (
    <section ref={ref} className="relative py-14">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF] mb-5">— How we deliver</div>
        <h2 className="font-display section-heading font-semibold max-w-3xl">
          Seven steps. Predictable delivery. <span className="gradient-text">Zero surprises.</span>
        </h2>

        <div className="relative mt-6 pl-10 min-[390px]:pl-12 sm:pl-20">
          {/* Track */}
          <div className="absolute left-3 sm:left-7 top-0 bottom-0 w-px bg-white/8"/>
          <motion.div style={{ height: lineH }} className="absolute left-3 sm:left-7 top-0 w-px bg-gradient-to-b from-[#1E6BFF] via-[#4D8BFF] to-transparent"/>

          <div className="space-y-9">
            {PROCESS_STEPS.map((s, i) => {
              const Icon = Lucide[s.icon] || Sparkles;
              return (
                <motion.div
                  key={s.num}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5 }}
                  className="relative"
                >
                  <div className="absolute -left-[31px] min-[390px]:-left-[34px] sm:-left-[58px] top-1">
                    <motion.div
                      initial={{ scale: 0.45, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true, amount: 0.7 }}
                      transition={{ duration: 0.42, delay: i * 0.08, ease: MOTION_EASE }}
                      className="w-7 h-7 sm:w-9 sm:h-9 rounded-full glass-strong flex items-center justify-center text-[#4D8BFF]"
                    >
                      <Icon size={13}/>
                    </motion.div>
                  </div>
                  <div className="flex items-baseline gap-4">
                    <span className="font-display text-[#3B4660] text-[14px]">{s.num}</span>
                    <h3 className="font-display text-[18px] sm:text-[22px] font-semibold">{s.title}</h3>
                  </div>
                  <p className="mt-2 text-[14.5px] text-[#9AA3B8] leading-relaxed max-w-2xl">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========================================================================
   07. TECH MARQUEE — animated, dual-row
   ======================================================================== */
function TechMarquee() {
  const all = TECH_GROUPS.flatMap((g) => g.items);
  const row1 = [...all, ...all];
  const reversed = [...all].reverse();
  const row2 = [...reversed, ...reversed];
  return (
    <section className="relative py-14">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-6">
        <div className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF] mb-5">— The stack</div>
        <h2 className="font-display section-heading font-semibold max-w-3xl">
          Modern, polyglot, <br/>chosen for <span className="gradient-text">fit</span>.
        </h2>
      </div>
      <div className="marquee-shell space-y-3 [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]">
        <div className="overflow-hidden">
          <div className="flex gap-3 marquee-track w-max">
            {row1.map((t, i) => (
              <div key={i} className="px-5 py-2.5 rounded-full glass text-[13px] text-[#C9D2E0] whitespace-nowrap">{t}</div>
            ))}
          </div>
        </div>
        <div className="overflow-hidden">
          <div className="flex gap-3 marquee-track w-max" style={{ animationDirection: "reverse", animationDuration: "44s" }}>
            {row2.map((t, i) => (
              <div key={i} className="px-5 py-2.5 rounded-full border border-white/8 bg-white/[0.02] text-[13px] text-[#8892A6] whitespace-nowrap">{t}</div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ========================================================================
   08. TEAM SHORT — horizontal card scroll teaser
   ======================================================================== */
function TeamTeaser() {
  return (
    <section className="relative py-14">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-end justify-between gap-5 mb-6">
          <div>
            <div className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF] mb-5">— The team</div>
            <h2 className="font-display section-heading font-semibold max-w-2xl">
              Passionate builders. <span className="text-[#4D8BFF]">Quality-first by habit.</span>
            </h2>
          </div>
          <Link to="/team" className="hidden sm:inline-flex btn-ghost">Meet everyone <ArrowRight size={15}/></Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {TEAM.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.5 }}
              className="card-glow p-4 aspect-[3/4] flex flex-col justify-end relative overflow-hidden"
            >
              <div className="absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F1830] via-[#0A0F1C] to-[#0F1830]"/>
                <div className="absolute inset-0 bg-grid opacity-30"/>
                <div className="absolute -top-5 -right-6 w-32 h-32 rounded-full bg-[#1E6BFF]/15 blur-2xl"/>
                <div className="absolute top-5 left-6 font-display text-[36px] gradient-text opacity-90">
                  {m.name.split(" ").map(s => s[0]).slice(0, 2).join("")}
                </div>
              </div>
              <div className="relative z-10">
                <div className="font-display text-white text-[14.5px] font-semibold leading-tight">{m.name}</div>
                <div className="text-[10.5px] uppercase tracking-[0.12em] text-[#4D8BFF] mt-1.5 line-clamp-1">{m.role}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ========================================================================
   09. INDUSTRIES — tag cloud with subtle scale
   ======================================================================== */
/* ========================================================================
   09. INDUSTRIES — minimal enterprise chips
   ======================================================================== */
function Industries() {
  return (
    <section className="relative py-14">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[11.5px] tracking-[0.22em] uppercase text-[#4D8BFF] mb-5">— Industries we empower</div>
          <h2 className="font-display section-heading font-medium">
            Industries we empower.
          </h2>
          <p className="mt-5 text-[15.5px] sm:text-[16px] text-[#9AA3B8] leading-relaxed">
            We build intelligent digital solutions tailored to the unique challenges of every industry we serve.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {INDUSTRIES.map((ind, i) => (
            <motion.span
              key={ind.name}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.34, delay: Math.min(i, 18) * 0.035, ease: MOTION_EASE }}
              className="px-3.5 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-[13px] text-[#C9D2E0] hover:text-white hover:border-[#1E6BFF]/40 hover:bg-white/[0.04] transition-colors duration-200"
            >
              {ind.name}
            </motion.span>
          ))}

          {/* Accent chip */}
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.25 }}
            className="px-3.5 py-1.5 rounded-full border border-[#1E6BFF]/40 bg-[#1E6BFF]/10 text-[13px] text-[#9DB8FF] hover:text-white hover:bg-[#1E6BFF]/20 hover:border-[#1E6BFF]/60 transition-colors duration-200 inline-flex items-center gap-1"
          >
            <span className="text-[#4D8BFF]">+</span> Many More Industries
          </motion.span>
        </div>
      </div>
    </section>
  );
}

/* ========================================================================
   10. FAQ — compact accordion
   ======================================================================== */
function FAQTeaser() {
  const [open, setOpen] = useState(0);
  const list = FAQS.slice(0, 6);
  return (
    <section className="relative py-14">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="text-[11.5px] tracking-[0.2em] uppercase text-[#4D8BFF] mb-5">— FAQ</div>
          <h2 className="font-display section-heading font-semibold">
            Common questions, <br/>honestly answered.
          </h2>
          <Link to="/faq" className="mt-6 inline-flex btn-ghost">All questions <ArrowRight size={15}/></Link>
        </div>
        <div className="lg:col-span-8 glass rounded-2xl divide-y divide-white/8">
          {list.map((f, i) => (
            <motion.button
              key={i}
              data-testid={`faq-item-${i}`}
              onClick={() => setOpen(open === i ? -1 : i)}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.42, delay: i * 0.055, ease: MOTION_EASE }}
              className="w-full text-left px-4 sm:px-6 py-5 hover:bg-white/[0.03] transition"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="font-display text-white font-medium text-[15.5px]">{f.q}</span>
                <Plus size={16} className={`text-[#4D8BFF] transition-transform ${open === i ? "rotate-45" : ""}`}/>
              </div>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: MOTION_EASE }}
                    className="overflow-hidden"
                  >
                    <p className="mt-3 text-[14px] text-[#9AA3B8] leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ========================================================================
   11. FINAL CTA — large editorial moment
   ======================================================================== */
function FinalCTA() {
  const reduceMotion = useReducedMotion();

  return (
    <section className="relative py-14">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28, scale: 0.985 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.72, ease: MOTION_EASE }}
          className="relative rounded-3xl overflow-hidden border border-white/10 p-5 sm:p-6 lg:p-8"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A1430] via-[#05080F] to-[#0A1430]"/>
          <div className="absolute inset-0 bg-grid opacity-30"/>
          <motion.div
            animate={reduceMotion ? {} : { scale: [1, 1.14, 1], opacity: [0.16, 0.3, 0.16] }}
            transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full bg-[#1E6BFF]/20 blur-[80px]"
          />
          <motion.div
            animate={reduceMotion ? {} : { x: ["-8%", "8%", "-8%"], opacity: [0.08, 0.2, 0.08] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -bottom-32 -left-24 w-[360px] h-[360px] rounded-full bg-[#4D8BFF]/15 blur-[72px]"
          />

          <div className="relative">
            <Quote className="text-[#4D8BFF] mb-4" size={28}/>
            <h3 className="font-display section-heading font-semibold max-w-3xl">
              Have a problem worth <span className="italic font-normal text-[#4D8BFF]">solving</span>?
            </h3>
            <p className="mt-5 text-[15.5px] text-[#9AA3B8] max-w-xl leading-relaxed">
              Tell us about your business and what you're trying to change. We reply within one working day — usually faster — and we mean it.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <MagneticButton strength={16}>
                <Link to="/contact" data-testid="cta-start-conversation" className="btn-primary !py-3 !px-6 text-[14px]">
                  Start a conversation <ArrowUpRight size={17}/>
                </Link>
              </MagneticButton>
              <a href="mailto:support@dortxtech.com" className="btn-ghost">support@dortxtech.com</a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ======================================================================== */
export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <DortXLive />
      <WhyExist />
      <Problems />
      <Approach />
      <Wings />
      <ProcessTimeline />
      <TechMarquee />
      <Industries />
      <FAQTeaser />
      <FinalCTA />
    </div>
  );
}
